import React, { useState, useEffect, useCallback } from 'react';
import { BrowserRouter as Router, Routes, Route, Navigate } from 'react-router-dom';
import { ethers } from 'ethers';
import Navbar from './components/Navbar';
import Home from './components/Home';
import Units from './components/Units';
import Team from './components/Team';
import Record from './components/Record';
import Mining from './components/Mining';
import Coin from './components/Coin';
import BuyNode from './components/BuyNode';
import BottomNav from './components/BottomNav';
import SmartcoreNav from './components/SmartcoreNav';
import TelegramModal from './components/TelegramModal';
import CosmicBackground from './components/CosmicBackground';
import ImagePopup from './components/ImagePopup';
import { getUserId, getUserDetails, getUserIncomeBreakdown } from './utils/contract';
import { getUserStats } from './utils/miningContract';
import './styles/base.css';
import './styles/components.css';
import './styles/theme.css';
import './styles/app.css';
import './styles/cosmic.css';

// Toast Component
const Toast = ({ toasts, removeToast }) => (
  <div className="toast-container">
    {toasts.map(toast => (
      <div key={toast.id} className={`toast toast-${toast.type}`}>
        <span>{toast.message}</span>
        <button onClick={() => removeToast(toast.id)}>×</button>
      </div>
    ))}
  </div>
);

function AppContent() {
  const [account, setAccount] = useState(null);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState(null);
  const [networkError, setNetworkError] = useState(false);
  const [activeTab, setActiveTab] = useState('smartcore');
  const [showTelegram, setShowTelegram] = useState(false);
  const [toasts, setToasts] = useState([]);
  
  const [userId, setUserId] = useState(null);
  const [userData, setUserData] = useState(null);
  const [incomeBreakdown, setIncomeBreakdown] = useState(null);
  const [miningData, setMiningData] = useState(null);

  const addToast = useCallback((type, message) => {
    const id = Date.now();
    setToasts(prev => [...prev, { id, type, message }]);
    setTimeout(() => setToasts(prev => prev.filter(t => t.id !== id)), 5000);
  }, []);

  const removeToast = useCallback((id) => {
    setToasts(prev => prev.filter(t => t.id !== id));
  }, []);

  const loadUserData = useCallback(async () => {
    if (!account) return;
    try {
      const id = await getUserId(account);
      if (id && id !== '0') {
        setUserId(id);
        const details = await getUserDetails(id);
        const breakdown = await getUserIncomeBreakdown(id);
        setUserData(details);
        setIncomeBreakdown(breakdown);
      }
      const mining = await getUserStats(account);
      setMiningData(mining);
    } catch (error) {
      console.error('Error loading user data:', error);
    }
  }, [account]);

  useEffect(() => {
    if (account) loadUserData();
  }, [account, loadUserData]);

  useEffect(() => {
    const checkConnection = async () => {
      if (typeof window.ethereum !== 'undefined') {
        try {
          const accounts = await window.ethereum.request({ method: 'eth_accounts' });
          if (accounts && accounts.length > 0) {
            const provider = new ethers.providers.Web3Provider(window.ethereum);
            const network = await provider.getNetwork();
            if (network.chainId === 204) {
              setAccount(accounts[0]);
              setNetworkError(false);
            } else {
              setNetworkError(true);
              setError('⚠️ Please switch to opBNB Mainnet');
            }
          }
        } catch (err) {
          console.error("Auto-connect failed:", err);
        }
      }
    };
    checkConnection();

    if (window.ethereum) {
      const handleAccountsChanged = (accounts) => {
        if (accounts.length === 0) {
          setAccount(null);
          setTimeout(() => window.location.reload(), 500);
        } else {
          setAccount(accounts[0]);
          setError(null);
        }
      };
      
      const handleChainChanged = (chainIdHex) => {
        const chainId = parseInt(chainIdHex, 16);
        if (chainId === 204) {
          setNetworkError(false);
          setError(null);
        } else {
          setNetworkError(true);
          setError('⚠️ Please switch to opBNB Mainnet');
        }
      };
      
      window.ethereum.on('accountsChanged', handleAccountsChanged);
      window.ethereum.on('chainChanged', handleChainChanged);
      
      return () => {
        if (window.ethereum.removeListener) {
          window.ethereum.removeListener('accountsChanged', handleAccountsChanged);
          window.ethereum.removeListener('chainChanged', handleChainChanged);
        }
      };
    }
  }, []);

  const handleConnect = async () => {
    if (typeof window.ethereum === 'undefined') {
      setError('No wallet detected. Please install MetaMask or Trust Wallet.');
      return;
    }
    
    setLoading(true);
    setError(null);
    
    try {
      const accounts = await window.ethereum.request({ method: 'eth_requestAccounts' });
      const provider = new ethers.providers.Web3Provider(window.ethereum);
      const network = await provider.getNetwork();
      
      if (network.chainId !== 204) {
        try {
          await window.ethereum.request({
            method: 'wallet_switchEthereumChain',
            params: [{ chainId: '0xCC' }],
          });
          setAccount(accounts[0]);
          setNetworkError(false);
        } catch (switchError) {
          if (switchError.code === 4902) {
            await window.ethereum.request({
              method: 'wallet_addEthereumChain',
              params: [{
                chainId: '0xCC',
                chainName: 'opBNB Mainnet',
                nativeCurrency: { name: 'BNB', symbol: 'BNB', decimals: 18 },
                rpcUrls: ['https://opbnb-mainnet-rpc.bnbchain.org'],
                blockExplorerUrls: ['https://opbnbscan.com/']
              }]
            });
            setAccount(accounts[0]);
            setNetworkError(false);
          } else {
            setError('Please switch to opBNB Mainnet');
          }
        }
      } else {
        setAccount(accounts[0]);
        setNetworkError(false);
      }
    } catch (error) {
      setError(`Connection error: ${error.message}`);
    } finally {
      setLoading(false);
    }
  };

  const handleNetworkSwitch = async () => {
    try {
      await window.ethereum.request({
        method: 'wallet_switchEthereumChain',
        params: [{ chainId: '0xCC' }],
      });
      setNetworkError(false);
      setError(null);
    } catch (error) {
      if (error.code === 4902) {
        await window.ethereum.request({
          method: 'wallet_addEthereumChain',
          params: [{
            chainId: '0xCC',
            chainName: 'opBNB Mainnet',
            nativeCurrency: { name: 'BNB', symbol: 'BNB', decimals: 18 },
            rpcUrls: ['https://opbnb-mainnet-rpc.bnbchain.org'],
            blockExplorerUrls: ['https://opbnbscan.com']
          }]
        });
      }
    }
  };

  const renderTabContent = () => {
    switch(activeTab) {
      case 'smartcore':
        return (
          <>
            <SmartcoreNav />
            <Routes>
              <Route path="/" element={<Home account={account} onConnect={handleConnect} />} />
              <Route path="/units" element={<Units account={account} />} />
              <Route path="/team" element={account ? <Team account={account} /> : <Navigate to="/" />} />
              <Route path="/record" element={account ? <Record account={account} /> : <Navigate to="/" />} />
              <Route path="*" element={<Navigate to="/" />} />
            </Routes>
          </>
        );
      case 'mining':
        return <Mining account={account} addToast={addToast} />;
      case 'coin':
        return <Coin account={account} />;
      case 'buynode':
        return <BuyNode account={account} />;
      default:
        return null;
    }
  };

  return (
    <div className="App">
      <CosmicBackground />

      <ImagePopup src="/popup.jpg" />

      <Navbar
        account={account} 
        onConnect={handleConnect} 
        loading={loading}
        onShowTelegram={() => setShowTelegram(true)}
      />
      
      {error && (
        <div className="container">
          <div className="alert alert-error">
            <span>{error}</span>
            {networkError && (
              <button className="btn btn-primary pulse" onClick={handleNetworkSwitch}>
                🔄 Switch Network
              </button>
            )}
          </div>
        </div>
      )}

      <main className="main-content">
        {renderTabContent()}
      </main>

      <BottomNav activeTab={activeTab} onTabChange={setActiveTab} />

      {showTelegram && (
        <TelegramModal
          account={account}
          userId={userId}
          userData={userData}
          incomeBreakdown={incomeBreakdown}
          miningData={miningData}
          onClose={() => setShowTelegram(false)}
          addToast={addToast}
        />
      )}

      <Toast toasts={toasts} removeToast={removeToast} />

      {loading && (
        <div className="loading-overlay">
          <img src="/core.png" alt="Loading" className="loading-logo" />
          <div className="loading-spinner"></div>
          <p>Connecting...</p>
        </div>
      )}
    </div>
  );
}

function App() {
  return (
    <Router>
      <AppContent />
    </Router>
  );
}

export default App;
